import { SelectionCard } from '@jouzen/ecom-components/v3/SelectionCard';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import MembershipSelectionEnum from '@/app/enums/MembershipSelectionEnum';
import type WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';

import type { Order } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';

interface OneYearMembershipSelectionProps {
  readonly onMembershipSelection: (
    value: MembershipSelectionEnum | WarrantySelectionEnum,
  ) => void;
  readonly order: Order;
  readonly yearlyMembership: ListProduct | undefined;
}

const OneYearMembershipSelection = ({
  onMembershipSelection,
  order,
  yearlyMembership,
}: OneYearMembershipSelectionProps) => {
  const t = useTranslations();
  const { currencyCode } = useCurrency();

  const { price } = getPriceAndDiscount(yearlyMembership);
  const selectedMembership = order.membership.value as MembershipSelectionEnum;

  // analytics tracking for membership selection and checkbox change
  // this is used to track the selection of one-year membership
  const handleMembershipSelection = async (): Promise<void> => {
    if (selectedMembership === MembershipSelectionEnum.OneYear) {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'pay as you go',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelectionEnum.OneMonth);
    } else {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'buy 12 months up front',
        location: 'pdp',
        step: 3,
      });
      onMembershipSelection(MembershipSelectionEnum.OneYear);
    }
  };

  return (
    <div
      className="row-start-2 border-b border-gray-300 pb-10 md:border-b-[0px] md:pb-0"
      data-cy="membership-section"
    >
      <SelectionCard
        cardSelected={selectedMembership === MembershipSelectionEnum.OneYear}
        currencyCode={currencyCode}
        data-cy="bundle-membership-one-year"
        onCheckBoxClick={handleMembershipSelection}
        price={price ?? 0}
        title={t('12_months_prepaid')}
      />
      <Typography className="italic" color="gray" variant="body-small">
        {t('add_ons_one_year_prepaid_copy')}
      </Typography>
    </div>
  );
};

export default OneYearMembershipSelection;
